import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { ModalPanel, ModalSidebarContext } from '../../../../../components/ModalSidebar'
import MicrosoftPortalLinks from '../common/MicrosoftPortalLinks'
import ScheduleSubmitSection from '../common/Scheduling/ScheduleSubmitSection'
import { isScheduleSelected } from '../common/Scheduling/fieldset'
import ReviewContent from './ReviewContent'
import { STEP_KEYS } from './constants'

const propTypes = {
  /**
   * Message of why the submission failed
   */
  submissionError: PropTypes.string,
  /**
   * Indicates if there was an error scheduling
   */
  errorSchedulingEngageTask: PropTypes.bool,
  /**
   * Employee full name
   */
  employeeFullName: PropTypes.string.isRequired,
  /**
   * Warnings returned from the completed offboarding task
   */
  successWarnings: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /**
   * information of the user to be removed
   */
  userInfo: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}

function SubmitPanel(props) {
  const {
    submissionError = null,
    errorSchedulingEngageTask = false,
    employeeFullName,
    successWarnings,
    userInfo = {},
  } = props ?? {}

  const { goToStep } = useContext(ModalSidebarContext)
  const { errors: validationErrors, values } = useFormikContext()

  const hasErrors = !isEmpty(validationErrors) || submissionError || errorSchedulingEngageTask
  const leftButton = hasErrors
    ? {
        label: 'GENERAL.BACK',
        variant: 'primary',
        onClick: () => goToStep(STEP_KEYS.DELETE_USER),
      }
    : null

  return (
    <ModalPanel leftButton={leftButton} rightButton="close">
      <SubmitPanelContent
        submissionError={submissionError}
        validationErrors={validationErrors}
        employeeFullName={employeeFullName}
        successWarnings={successWarnings}
        userInfo={userInfo}
        scheduleSelected={isScheduleSelected(values)}
        errorSchedulingEngageTask={errorSchedulingEngageTask}
      />
    </ModalPanel>
  )
}

/* eslint-disable react/prop-types */
function SubmitPanelContent(props) {
  const {
    submissionError,
    validationErrors,
    employeeFullName,
    successWarnings,
    userInfo,
    scheduleSelected,
    errorSchedulingEngageTask,
  } = props

  // formik runs the validation every time on submit
  // in this step the form should be already validated and good to go
  // however if anything happens show all errors here
  if (!isEmpty(validationErrors)) {
    return (
      <>
        <div>
          <span className="font-color-danger fas fa-circle-xmark me-2" />
          <span className="text-bold">
            <FormattedMessage id="GENERAL.MODAL_VALIDATION_FAILED" />
          </span>
        </div>
        <ul>
          {Object.entries(validationErrors).map(([key, value]) => (
            <li key={key}>
              {key}: {value}
            </li>
          ))}
        </ul>
      </>
    )
  }

  // scheduling case
  if (scheduleSelected) {
    return <ScheduleSubmitSection hasError={errorSchedulingEngageTask} />
  }

  if (submissionError) {
    return (
      <>
        <div>
          <span className="font-color-danger fas fa-circle-xmark me-2" />
          <span className="text-bold">
            <FormattedMessage id="ENGAGE.OFFBOARD.DELETE_EMPLOYEE_FAILED" />
          </span>
        </div>
        <ul>
          <li>{String(submissionError)}</li>
        </ul>
      </>
    )
  }

  // success case
  return (
    <>
      <ReviewContent
        isUserOffboarded
        employeeFullName={employeeFullName}
        successWarnings={successWarnings}
        userInfo={userInfo}
      />
      <hr className="mb-4" />
      <MicrosoftPortalLinks />
    </>
  )
}

SubmitPanel.propTypes = propTypes

export default SubmitPanel
